import { ChangeEvent, JSX, useCallback, useState } from "react";
import dayjs from "dayjs";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { YYYY_MM_DD } from "constants/date";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { UpdateFilterFunction, useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import {
  RecognitionWaterfallKind,
  RecognitionWaterfallOrdering,
  useGetRecognitionWaterfallQuery,
} from "store/apis/recognitionWaterfall";
import { useDebounce } from "utils/debounce";
import {
  CUSTOM_PERIOD,
  DEFAULT_ORDERING,
  DEFAULT_PAGE_SIZE,
  DESCENDING_PREFIX,
  LABELS,
  MAX_PAGE_SIZE,
  PAGE_QUERY_KEY,
  PAGE_SIZE_QUERY_KEY,
  PERIOD_COLUMN_PREFIX,
} from "./constants";
import { WaterfallFilters, WaterfallToolbar } from "./WaterfallToolbar";
import { PartyCellDetails, SelectedCell } from "./PartyCellDetails";
import { SelectCell } from "./useWaterfallColumns";
import { ROW_TYPE } from "./waterfallRows";
import { WaterfallTable } from "./WaterfallTable";

type RecognitionPartyWaterfallProps = {
  kind: RecognitionWaterfallKind;
};

// URL values can be hand-edited; keep them inside what the API accepts
const clampPageSize = (value: string | null) =>
  Math.min(Math.max(1, Number(value) || DEFAULT_PAGE_SIZE), MAX_PAGE_SIZE);

const clampPageNum = (value: string | null) => Math.max(1, Number(value) || 1);

export const RecognitionPartyWaterfall = ({
  kind,
}: RecognitionPartyWaterfallProps): JSX.Element => {
  const entityId = useCurrentEntityId();
  const labels = LABELS[kind];
  const query = useQuery();
  const { update, updateMultiple } = useUpdateQuery();
  // Page and page size live in the URL so a shared link opens on the same page
  const pageNum = clampPageNum(query.get(PAGE_QUERY_KEY));
  const pageSize = clampPageSize(query.get(PAGE_SIZE_QUERY_KEY));
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [ordering, setOrdering] =
    useState<RecognitionWaterfallOrdering>(DEFAULT_ORDERING);

  const { values, updateFilter } = useFilters<WaterfallFilters>({
    initialValue: {
      START_DATE: dayjs().startOf("year").format(YYYY_MM_DD),
      END_DATE: dayjs().endOf("year").format(YYYY_MM_DD),
      SELECT_PERIOD: CUSTOM_PERIOD,
    },
  });

  // A new page, search, period or sort no longer shows the clicked cell, so its slider closes
  const handlePageNumChange = (newPageNum: number) => {
    setSelectedCell(null);
    update({ query: PAGE_QUERY_KEY, value: newPageNum });
  };
  const resetPage = () => handlePageNumChange(1);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    resetPage();
  };

  const handleOrderingChange = (newOrdering: RecognitionWaterfallOrdering) => {
    setOrdering(newOrdering);
    resetPage();
  };

  const handleFilterUpdate: UpdateFilterFunction<WaterfallFilters> = (
    name,
    newValue
  ) => {
    updateFilter(name, newValue);
    // A month column is an index into the range, so it points at a different month once the range changes
    if (
      ordering.replace(DESCENDING_PREFIX, "").startsWith(PERIOD_COLUMN_PREFIX)
    ) {
      setOrdering(DEFAULT_ORDERING);
    }
    resetPage();
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setSelectedCell(null);
    updateMultiple([
      { query: PAGE_SIZE_QUERY_KEY, value: newPageSize },
      { query: PAGE_QUERY_KEY, value: 1 },
    ]);
  };

  const handleCloseCellDetails = () => setSelectedCell(null);

  // Stable so the table's columns aren't rebuilt on every render
  const handleSelectCell: SelectCell = useCallback((row, column, cellId) => {
    if (row.type === ROW_TYPE.PARTY) {
      setSelectedCell({ cellId, party: row.party, column });
    }
  }, []);

  const { data, isLoading, isFetching, isSuccess, isError } =
    useGetRecognitionWaterfallQuery(
      {
        entityId,
        kind,
        start_date: values.START_DATE,
        end_date: values.END_DATE,
        search_term: debouncedSearchTerm || undefined,
        ordering,
        page_num: pageNum,
        page_size: pageSize,
      },
      { skip: !entityId }
    );

  return (
    <div className="t-space-y-4">
      <WaterfallToolbar
        searchPlaceholder={labels.search}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        filters={values}
        onFilterUpdate={handleFilterUpdate}
        data={data}
        pageNum={pageNum}
        onPageNumChange={handlePageNumChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        isFetching={isFetching}
      />

      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        isEmpty={data?.total_count === 0}
      >
        <Async.ErrorHandler>
          <p className="t-text-body-sm t-text-red t-text-center">
            Couldn't load the waterfall. Please try again.
          </p>
        </Async.ErrorHandler>
        <Async.Empty>
          <p className="t-text-body-sm t-text-text-30 t-text-center">
            No {labels.documentPlural} with scheduled recognition in this period
          </p>
        </Async.Empty>
        <Async.Success>
          {data && (
            <WaterfallTable
              data={data}
              labels={labels}
              selectedCellId={selectedCell?.cellId ?? null}
              ordering={ordering}
              onOrderingChange={handleOrderingChange}
              onSelectCell={handleSelectCell}
            />
          )}
        </Async.Success>
      </Async.Root>

      {selectedCell && data && (
        <PartyCellDetails
          kind={kind}
          periods={data.periods}
          selected={selectedCell}
          onClose={handleCloseCellDetails}
        />
      )}
    </div>
  );
};
